import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_STORAGE_KEY, normalizeLocale } from '@/core/hooks/useAppLanguage';
import { useFixedTooltip } from '@/features/menu/hooks/useFixedTooltip';
import { useDebounce } from '@/features/header/hooks/useDebounce';
import { SearchDropdown, SearchResult } from './SearchDropdown';
import './TopBar.scss';
import { filterSearchResults, isValidSearchTerm } from '@/features/header/utils/searchUtils';
import { BrandLogo } from '@/core/components/BrandLogo/BrandLogo';
import {
  BdsIcon,
  BdsAvatar,
  BdsThemeProvider,
} from '@/core/constants/webComponents';
import { HEADER_EVENT_NAMES as EVENT_NAMES } from '@/features/header/constants/eventNames';
import { sanitizeDisplayText } from '@/core/utils/sanitizeDisplayText';

type ExternalItems = SearchResult[] | string;

const SCROLL_HIDE_START_Y = 120;
const SCROLL_DELTA_TO_HIDE = 16;
const SCROLL_DELTA_TO_SHOW = 8;


const resolveCurrentLanguage = (
  hideLanguageSelector: boolean,
  selectedLanguage: string | null,
) => {
  if (hideLanguageSelector) return 'pt';
  const stored = selectedLanguage ?? localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? 'pt';
  return normalizeLocale(stored);
};

const parseExternalItems = (rawItems?: ExternalItems): SearchResult[] | null => {
  if (!rawItems) return null;

  if (Array.isArray(rawItems)) {
    return rawItems;
  }

  try {
    const parsed = JSON.parse(rawItems);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.error('[TopBar] Error parsing external items:', error);
    return null;
  }
};


export interface TopBarProps {
  userData?: string | {
    userId?: string;
    username?: string;
    initials?: string;
    avatar?: string;
  };
  reprocessingStatus?: string;
  availableItems?: SearchResult[];
  hideLanguageSelector?: boolean;
  hideSearchBar?: boolean;
  hideReprocessingStatus?: boolean;
  hideSidebarButton?: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({
  userData,
  reprocessingStatus,
  availableItems = [],
  hideLanguageSelector = false,
  hideSearchBar = false,
  hideReprocessingStatus = false,
  hideSidebarButton = false,
}) => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const handleTooltipPosition = useFixedTooltip('.top-bar__custom-tooltip', 'top');

  useEffect(() => {
    const syncSelectedLanguageFromEvent = (event: Event) => {
      const languageEvent = event as CustomEvent<{ language?: string }>;
      const nextLanguage = normalizeLocale(languageEvent.detail?.language ?? null);
      setSelectedLanguage((currentLanguage) =>
        currentLanguage === nextLanguage ? currentLanguage : nextLanguage
      );
    };

    const syncSelectedLanguageFromStorage = (event: StorageEvent) => {
      if (event.key !== LANGUAGE_STORAGE_KEY) {
        return;
      }

      const nextLanguage = normalizeLocale(event.newValue);
      setSelectedLanguage((currentLanguage) =>
        currentLanguage === nextLanguage ? currentLanguage : nextLanguage
      );
    };

    document.addEventListener(EVENT_NAMES.CHANGE_LANGUAGE, syncSelectedLanguageFromEvent as EventListener);
    window.addEventListener('storage', syncSelectedLanguageFromStorage);

    return () => {
      document.removeEventListener(EVENT_NAMES.CHANGE_LANGUAGE, syncSelectedLanguageFromEvent as EventListener);
      window.removeEventListener('storage', syncSelectedLanguageFromStorage);
    };
  }, []);
  const currentLanguage = resolveCurrentLanguage(hideLanguageSelector, selectedLanguage);

  const [searchTerm, setSearchTerm] = useState('');
  const [externalItems, setExternalItems] = useState<SearchResult[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const allAvailableItems = useMemo(() => {
    return [...availableItems, ...externalItems];
  }, [availableItems, externalItems]);

  const activeSearchTerm = useMemo(() => {
    const parts = searchTerm.split(',').map(p => p.trim());
    return parts[parts.length - 1] || '';
  }, [searchTerm]);

  const debouncedActiveSearchTerm = useDebounce(activeSearchTerm, 300);

  const searchResults = useMemo(() => {
    if (!isValidSearchTerm(debouncedActiveSearchTerm)) {
      return [];
    }
    return filterSearchResults(debouncedActiveSearchTerm, allAvailableItems);
  }, [debouncedActiveSearchTerm, allAvailableItems]);

  useEffect(() => {
    const handleExternalUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ items: SearchResult[] | string }>;
      const parsedItems = parseExternalItems(customEvent.detail?.items);

      if (parsedItems) {
        setExternalItems(parsedItems);
      }
    };

    document.addEventListener(EVENT_NAMES.UPDATE_EXTERNAL_ITEMS, handleExternalUpdate);
    return () => {
      document.removeEventListener(EVENT_NAMES.UPDATE_EXTERNAL_ITEMS, handleExternalUpdate);
    };
  }, []);

  // Calculates already-confirmed selections (all comma-separated parts except the last active term)
  const confirmedSelections = useMemo(() => {
    const parts = searchTerm.split(',').map((p) => p.trim());
    return new Set(parts.slice(0, -1).filter(Boolean));
  }, [searchTerm]);

  // Filters out already-selected items from the dropdown results
  const displaySearchResults = useMemo(() => {
    return searchResults.filter((result) => {
      const label = result.label ?? result.name;
      return !confirmedSelections.has(label);
    });
  }, [searchResults, confirmedSelections]);

  // Calculates dropdown visibility using the filtered list
  const isSearchDropdownVisible = isFocused && displaySearchResults.length > 0;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const brandBtnRef = useRef<HTMLButtonElement>(null);
  const refreshBtnRef = useRef<HTMLButtonElement>(null);
  const statusBtnRef = useRef<HTMLButtonElement>(null);
  const guideBtnRef = useRef<HTMLButtonElement>(null);
  const langSelectorBtnRef = useRef<HTMLButtonElement>(null);
  const profileBtnRef = useRef<HTMLButtonElement>(null);
  const sidebarBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  const dispatch = useCallback((eventName: string, detail?: any) => {
    const target = containerRef.current ?? document;
    target.dispatchEvent(new CustomEvent(eventName, { 
      detail,
      bubbles: true,
      composed: true 
    }));
  }, []);

  const handleLanguageChange = useCallback((newLang: string) => {
    const normalizedLanguage = normalizeLocale(newLang);
    setSelectedLanguage(normalizedLanguage);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
    dispatch(EVENT_NAMES.CHANGE_LANGUAGE, { language: normalizedLanguage });
    setIsLangOpen(false);
  }, [dispatch]);

  useEffect(() => {
    if (isValidSearchTerm(debouncedActiveSearchTerm)) {
      dispatch(EVENT_NAMES.SEARCH_RESULTS, { term: debouncedActiveSearchTerm, results: searchResults });
    }
  }, [debouncedActiveSearchTerm, searchResults, dispatch]);

  const handleSearchInput = useCallback((term: string) => {
    setSearchTerm(term);
    dispatch(EVENT_NAMES.SEARCH_INPUT_CHANGE, { term });
  }, [dispatch]);

  const handleSearchSelect = useCallback((result: SearchResult) => {
    const isProfile = result.type !== 'group' && result.type !== 'theme';
    const resultLabel = result.label ?? result.name;

    if (isProfile) {
      setSearchTerm(resultLabel);
      setIsFocused(false);
      setTimeout(() => setSearchTerm(''), 2000);
    } else {
      const parts = searchTerm.split(',').map(p => p.trim());
      parts.pop();
      parts.push(resultLabel);
      const newValue = parts.join(', ') + ', ';

      setSearchTerm(newValue);
      setIsFocused(true);
      if (searchInputRef.current) {
        searchInputRef.current.value = newValue;
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            setIsFocused(true);
            const len = searchInputRef.current.value.length;
            searchInputRef.current.setSelectionRange(len, len);
            searchInputRef.current.scrollLeft = searchInputRef.current.scrollWidth;
          }
        }, 50);
      }
    }
    dispatch(EVENT_NAMES.SEARCH_SELECT, { result });
  }, [searchTerm, dispatch]);

  const handleSearchSubmit = useCallback(() => {
    const selectedTerms = searchTerm
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);
    
    dispatch(EVENT_NAMES.SEARCH_SUBMIT, { 
      searchTerm, 
      selectedTerms 
    });

    setSearchTerm('');
  }, [searchTerm, dispatch]);

  const [show, setShow] = useState(true);
  const lastScrollY = useRef(0);

  const controlNavbar = useCallback(() => {
    const currentScrollY = window.scrollY;
    const delta = currentScrollY - lastScrollY.current;

    // Keeps header visible near the top and avoids toggling on tiny scroll changes.
    if (currentScrollY <= SCROLL_HIDE_START_Y) {
      setShow(true);
      lastScrollY.current = currentScrollY;
      return;
    }

    if (delta > SCROLL_DELTA_TO_HIDE) {
      setShow(false);
    } else if (delta < -SCROLL_DELTA_TO_SHOW) {
      setShow(true);
    }

    lastScrollY.current = currentScrollY;
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', controlNavbar);
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [controlNavbar]);

  useEffect(() => {
    const attach = (ref: React.RefObject<HTMLElement | null>, type: string, handler: EventListener) => {
      const el = ref.current;
      if (!el) return () => {};
      el.addEventListener(type, handler);
      return () => el.removeEventListener(type, handler);
    };

    const cleanup: (() => void)[] = [
      attach(searchInputRef, 'focus', () => {
        dispatch(EVENT_NAMES.SEARCH_INPUT_FOCUS);
        setIsFocused(true);
      }),
      attach(searchInputRef, 'keydown', (e) => {
        const keyboardEvent = e as KeyboardEvent;
        if (keyboardEvent.key === 'Escape') {
          setSearchTerm('');
          dispatch(EVENT_NAMES.SEARCH_CLEAR);
        }
        if (keyboardEvent.key === 'Enter') {
          handleSearchSubmit();
        }
      }),
      attach(searchInputRef, 'blur', () => {
        setTimeout(() => {
          if (document.activeElement !== searchInputRef.current) {
            setIsFocused(false);
          }
        }, 300);
      }),
      attach(brandBtnRef, 'click', () => dispatch(EVENT_NAMES.BRAND_CLICK)),
      attach(refreshBtnRef, 'click', () => dispatch(EVENT_NAMES.BUTTON_RELOAD)),
      attach(statusBtnRef, 'click', () => dispatch(EVENT_NAMES.BUTTON_STATUS)),
      attach(guideBtnRef, 'click', () => dispatch(EVENT_NAMES.BUTTON_GUIDE)),
      attach(sidebarBtnRef, 'click', () => dispatch(EVENT_NAMES.SIDEBAR_TOGGLE)),
      attach(langSelectorBtnRef, 'click', () => setIsLangOpen((prev) => !prev)),
      attach(langSelectorBtnRef, 'blur', () => setTimeout(() => setIsLangOpen(false), 200)),
      attach(profileBtnRef, 'click', () => dispatch(EVENT_NAMES.BUTTON_PROFILE)),
    ];

    return () => cleanup.forEach(fn => fn());
  }, [
    dispatch,
    handleSearchInput,
    handleSearchSubmit,
    isLangOpen,
  ]);


  const parsedUserData = useMemo(() => {
    if (typeof userData === 'object') return userData;
    try {
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }, [userData]);

  const profileDisplayName = sanitizeDisplayText(parsedUserData?.username || parsedUserData?.userName);

  return (
    <BdsThemeProvider theme="dark">
      <div ref={containerRef} className={`top-bar ${show ? '' : 'top-bar-hidden'}`}>
        <div className="top-bar__logo-section">
          {!hideSidebarButton ? (
            <div className="top-bar__sidebar-controller">
              <button
                ref={sidebarBtnRef}
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                aria-label={t('header.toggleSidebar')}
              >
                <BdsIcon size="medium" name="menu-hamburger" color="surface-0"></BdsIcon>
              </button>
            </div>
          ) : (
            <div className="top-bar__sidebar-placeholder" />
          )}
          <button
            ref={brandBtnRef}
            className="top-bar__brand-button"
            aria-label={t('header.home')}
          >
            <BrandLogo
              className="top-bar__brand"
              illustrationWrapperClassName="top-bar__brand-blip"
              illustrationClassName="top-bar__brand-icon"
              altText={t('common.brandIcon')}
            />
          </button>
        </div>
        <div className="top-bar__content">
        {!hideSearchBar && (
          <div className="top-bar__search-wrapper">
            <button
              className="top-bar__search-help top-bar__tooltip-wrapper"
              onMouseEnter={handleTooltipPosition}
            >
              <BdsIcon name="question" theme="outline" style={{ transform: 'scale(0.7)' }}></BdsIcon>
              <span className="top-bar__custom-tooltip" role="tooltip">
                {t('header.searchHelpTitle')}
              </span>
            </button>
            <label className="top-bar__search">
              <button 
                className="top-bar__search-icon-container"
                onClick={handleSearchSubmit}
                type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                aria-label={t('header.searchSubmit')}
              >
                <BdsIcon name="search" theme="outline"></BdsIcon>
              </button>
              <input
                ref={searchInputRef}
                type="text"
                className="top-bar__search-input"
                placeholder={t('header.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => handleSearchInput(e.target.value)}
              />
            </label>
            <SearchDropdown 
              results={displaySearchResults} 
              isVisible={isSearchDropdownVisible} 
              onSelect={handleSearchSelect}
            />
          </div>
        )}

          <div className="top-bar__actions">
            <button 
              ref={refreshBtnRef}
              className="top-bar__action-button top-bar__tooltip-wrapper" 
              onMouseEnter={handleTooltipPosition}
            >
              <BdsIcon name="refresh" theme="outline"></BdsIcon>
              <span className="top-bar__custom-tooltip" role="tooltip">
                {t('header.refreshTitle')}
              </span>
            </button>

            {!hideReprocessingStatus && (
              <button 
                ref={statusBtnRef}
                data-testid="reprocessing-status-button"
                className="top-bar__action-button top-bar__tooltip-wrapper"
                onMouseEnter={handleTooltipPosition}
              >
                <BdsIcon 
                  name="info"
                  theme={reprocessingStatus === 'active' ? 'solid' : 'outline'}
                ></BdsIcon>
                <span className="top-bar__custom-tooltip" role="tooltip">
                  {reprocessingStatus ? `Status: ${reprocessingStatus}` : t('header.statusTitle')}
                </span>
              </button>
            )}

            <button 
              ref={guideBtnRef}
              className="top-bar__action-button top-bar__tooltip-wrapper" 
              onMouseEnter={handleTooltipPosition}
            >
              <BdsIcon name="book" theme="outline"></BdsIcon>
              <span className="top-bar__custom-tooltip" role="tooltip">
                {t('header.helpTitle')}
              </span>
            </button>
            
            {!hideLanguageSelector && (
              <div className="top-bar__language-selector">
                <button
                  ref={langSelectorBtnRef}
                  className={`top-bar__language-button ${isLangOpen ? 'top-bar__language-button--open' : ''}`}
                >
                  <BdsIcon name="site" theme="outline"></BdsIcon>
                  <span className="top-bar__language-current">{currentLanguage}</span>
                  <BdsIcon 
                    name="arrow-down" 
                    theme="outline" 
                    style={{ transform: 'scale(0.8)' }}
                  ></BdsIcon>
                </button>
                {isLangOpen && (
                  <ul className="top-bar__language-dropdown">
                    <li><button type="button" onClick={() => handleLanguageChange('pt')}>{t('header.languages.pt')}</button></li>
                    <li><button type="button" onClick={() => handleLanguageChange('en')}>{t('header.languages.en')}</button></li>
                    <li><button type="button" onClick={() => handleLanguageChange('es')}>{t('header.languages.es')}</button></li>
                  </ul>
                )}
              </div>
            )}

            <button 
              ref={profileBtnRef}
              className="top-bar__profile-button top-bar__tooltip-wrapper" 
              onMouseEnter={handleTooltipPosition}
            >
              <BdsAvatar size="extra-small" name={profileDisplayName} thumbnail={parsedUserData?.avatar ?? ''}></BdsAvatar>
              <span className="top-bar__custom-tooltip top-bar__custom-tooltip--right-aligned" role="tooltip">
                {profileDisplayName ?? t('header.profileTitle')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </BdsThemeProvider>
  );
};

export type { SearchResult };
